import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import {CurrentUser, User} from "types/User";
import DeleteAudienceMember from '../Delete';
import StatList from "components/StatList";
import StatBox from "components/StatBox";
import {
  CalculatorIcon,
  CalendarIcon,
  CashIcon,
  CurrencyPoundIcon,
  FastForwardIcon,
  FlagIcon,
  IdentificationIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
  TicketIcon,
  TruckIcon
} from "@heroicons/react/outline";
import {OperatorSettings} from "types/Operator";
import {useOperator} from "context/OperatorContext";
import {useAuth} from "context/AuthContext";
import Divider from "components/Divider";
import SectionLinks from "components/SectionLinks";
import TicketsCard from "components/AudienceMember/TicketsCard";
import MembershipsCard from "components/AudienceMember/MembershipsCard";
import VisitsCard from "components/AudienceMember/VisitsCard";
import VouchersCard from "components/AudienceMember/VouchersCard";
import SponsorshipsCard from "components/AudienceMember/SponsorshipsCard";
import FeedbackCard from "components/AudienceMember/FeedbackCard";
import QuizzesCard from "components/AudienceMember/QuizzesCard";
import AccountCard from "components/AudienceMember/AccountCard";
import ProfileCard from "components/AudienceMember/ProfileCard";
import FeatureFlag from "types/FeatureFlag";
import {UnitSystem} from "types/UnitSystem";
import convert from "convert";
import ContactsCard from "../../../components/AudienceMember/ContactsCard";
import {AppPermissionsCard} from "../../../components/AudienceMember/AppPermissionsCard";

// Helpers ----

function userIsProtected(userId: number, currentUser: CurrentUser | null, settings?: OperatorSettings): boolean {
  return [1, currentUser?.id, settings?.audience.guest_user_id]
    .filter(Boolean)
    .includes(userId)
}

function formatLength(value: number, system: UnitSystem): string {
  if(value <= 0) return '0';

  return convert(value, 'metres')
    .to('best', system)
    .toString()
}

// Component ----

function ViewAudienceMember() {
  const {settings, termFor} = useOperator();
  const {user, canUse, roles} = useAuth();
  const {id} = useParams();

  const {data: audienceMember, isLoading: isLoadingAudienceMember} = useQuery(
    ['organisation', {id}],
    () => ngage.users.get(Number(id)),
    {keepPreviousData: true}
  )

  const {data: insights, isLoading: isLoadingInsights} = useQuery(
    ['user.insights', {id}],
    () => ngage.users.insights(Number(id)),
    {keepPreviousData: true}
  )

  const [deletingAudienceMember, setDeletingAudienceMember] = useState<User | null>(null);
  const navigate = useNavigate()

  // Prepare action handlers
  function handleDeleteAudienceMember() {
    if (audienceMember) setDeletingAudienceMember(audienceMember)
  }

  function handleEditAudienceMember() {
    if (id) navigate(`/audience/${id}/edit`)
  }

  // Prepare breadcrumbs
  const breadcrumbs = {
    'Marketing': '/',
    'Audience': '/audience',
    [audienceMember?.name || "View Audience Member"]: `/audience/${id}`
  }

  // Prepare page actions
  const actions = <>
    {/* TODO: This is a temporary solution to prevent Audience Managers deleting audience members */}
    {!roles.includes('audience manager') &&
      <Button
        color={Button.colors.WHITE}
        icon={TrashIcon}
        onClick={handleDeleteAudienceMember}
        disabled={!audienceMember || userIsProtected(audienceMember.id, user, settings)}
      >
        Delete
      </Button>
    }
    <Button
      color={Button.colors.PRIMARY}
      icon={PencilIcon}
      onClick={handleEditAudienceMember}
      disabled={!audienceMember}
    >
      Edit
    </Button>
  </>

  return (
    <Page
      title={audienceMember?.name || "View Audience Member"}
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoadingAudienceMember || isLoadingInsights}
    >
      {audienceMember &&
        <>
          {/* Audience Member Stats */}
          <section>
            <StatList columns={4}>
              <StatBox
                name="Total Spent"
                icon={CurrencyPoundIcon}
                stat={insights?.engagement.ticket_spend_formatted}
              />
              <StatBox
                name="Tickets"
                icon={TicketIcon}
                stat={insights?.engagement.all_tickets}
              />
              <StatBox
                name="Memberships"
                icon={IdentificationIcon}
                stat={insights?.engagement.all_memberships}
              />
              <StatBox
                name={termFor('Sponsorships')}
                icon={CashIcon}
                stat={insights?.sponsorship.all_sponsorships}
              />
              <StatBox
                name={termFor('Visits')}
                icon={TruckIcon}
                stat={insights?.engagement.all_visits}
              />
              <StatBox
                name={termFor('Events')}
                icon={CalendarIcon}
                stat={insights?.events.events_attended}
              />
              <StatBox
                name="Trail Attempts"
                icon={FlagIcon}
                stat={insights?.engagement.all_trail_attempts}
              />
              <StatBox
                name="Quiz Attempts"
                icon={QuestionMarkCircleIcon}
                stat={insights?.education.all_quiz_attempts}
              />

              {canUse(FeatureFlag.Activities) &&
                <>
                  <StatBox
                    name="Activities"
                    icon={PlayIcon}
                    stat={(insights?.engagement.all_activities ?? 0).toLocaleString()}
                  />

                  <StatBox
                    name="Total Steps"
                    icon={FastForwardIcon}
                    stat={(insights?.engagement.total_steps ?? 0).toLocaleString()}
                  />

                  <StatBox
                    name="Total Distance"
                    icon={CalculatorIcon}
                    stat={formatLength(
                      insights?.engagement.total_distance ?? 0,
                      settings?.localisation.length_units ?? UnitSystem.METRIC
                    )}
                  />
                </>
              }
            </StatList>
          </section>

          <div className="mt-8 flow-root">
            <Divider/>
          </div>

          {/* Tab navigation */}
          <div className="mt-6">
            <SectionLinks links={[
              {name: 'Member', href: '#member'},
              {name: 'Account', href: '#account'},
              {name: 'App Permissions', href: '#app-permissions'},
              {name: 'Contacts', href: '#contacts'},
              {name: 'Tickets', href: '#tickets'},
              {name: 'Memberships', href: '#memberships'},
              {name: termFor('Visits'), href: '#visits'},
              {name: 'Vouchers', href: '#vouchers'},
              {name: 'Sponsorships', href: '#sponsorships'},
              {name: 'Feedback', href: '#feedback'},
              {name: 'Quizzes', href: '#quizzes'},
            ]}/>
          </div>

          {/* Audience Member Information */}
          <section id="member" className="mt-6">
            <ProfileCard audienceMember={audienceMember} isLoading={isLoadingAudienceMember} />
          </section>

          {/* Account Information */}
          <section id="account" className="mt-4">
            <AccountCard audienceMember={audienceMember} isLoading={isLoadingAudienceMember} />
          </section>

          {/* Permissions Information */}
          <section id="app-permissions" className="mt-4">
            <AppPermissionsCard permissions={audienceMember?.app_permissions} isLoading={isLoadingAudienceMember} />
          </section>

          {/* Contacts Information */}
          <section id="contacts" className="mt-4">
            <ContactsCard ownerId={Number(id)} />
          </section>

          {/* Tickets Information */}
          <section id="tickets" className="mt-4">
            <TicketsCard userId={Number(id)} insights={insights} />
          </section>

          {/* Memberships Information */}
          <section id="memberships" className="mt-4">
            <MembershipsCard userId={Number(id)} insights={insights} />
          </section>

          {/* Visits Information */}
          <section id="visits" className="mt-4">
            <VisitsCard userId={Number(id)} insights={insights} />
          </section>

          {/* Vouchers Information */}
          <section id="vouchers" className="mt-4">
            <VouchersCard userId={Number(id)} insights={insights} />
          </section>

          {/* Sponsorships Information */}
          <section id="sponsorships" className="mt-4">
            <SponsorshipsCard userId={Number(id)} insights={insights} />
          </section>

          {/* Feedback Information */}
          <section id="feedback" className="mt-4">
            <FeedbackCard userId={Number(id)} insights={insights} />
          </section>

          {/* Quizzes Information */}
          <section id="quizzes" className="mt-4">
            <QuizzesCard insights={insights} />
          </section>
        </>
      }

      {/* Delete confirmation modal */}
      <DeleteAudienceMember
        show={audienceMember === deletingAudienceMember}
        audienceMember={deletingAudienceMember}
        onCancel={() => setDeletingAudienceMember(null)}
        onDelete={() => navigate('/audience')}
        onDeleteFail={() => setDeletingAudienceMember(null)}
      />
    </Page>
  );
}

export default ViewAudienceMember;